import React from "react";
import { AlertCircle, Calendar, Download, LayoutDashboard, Receipt } from "lucide-react";

import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { TabSwitcher } from "components/ui/TabSwitcher";

import { PaymentModal } from "./components/PaymentModal";
import { PaymentsDashboard } from "./components/PaymentsDashboard";
import { PaymentsDueTab } from "./components/PaymentsDueTab";
import { usePaymentsActions } from "./hooks/usePaymentsActions";
import { usePaymentsColumns } from "./hooks/usePaymentsColumns";
import { usePaymentsData } from "./hooks/usePaymentsData";
import styles from "./PaymentsPage.module.css";

const PaymentsPage = () => {
  const {
    activeTab,
    dashboardStartDate,
    dashboardEndDate,
    setDashboardStartDate,
    setDashboardEndDate,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    filters,
    searchQuery,
    setSearchQuery,
    isLoadingPayments,
    paymentCalculations,
    paymentMetrics,
    allPaymentsTotalsMetrics,
    todayPaymentsTotalsMetrics,
    paymentTabOptions,
    handleTabChange,
    handleFilterChange,
    FILTERS_CONFIG,
    selection,
    paymentsDueData,
    paymentsDueTotalRecords,
    isLoadingPaymentsDue,
    paymentsDuePage,
    setPaymentsDuePage,
    paymentsDueRowsPerPage,
    setPaymentsDueRowsPerPage,
  } = usePaymentsData();

  const {
    paymentModalOpen,
    selectedPaymentForModal,
    loadingPaymentId,
    modalPaymentsDue,
    isModalPaymentsLoading,
    handlePaymentClick,
    handleClosePaymentModal,
    handleConfirmPayment,
    handleDownloadReceipt,
    handlePatientClick,
    handleExportUnpaid,
    handleExportAnonymous,
    handleBulkDownload,
  } = usePaymentsActions({
    filteredPayments: paymentCalculations.filteredPayments,
  });

  const { columns, rowActions } = usePaymentsColumns({
    handlePatientClick,
    handlePaymentClick,
    handleDownloadReceipt,
  });

  const currentMetrics =
    activeTab === "today" ? todayPaymentsTotalsMetrics : allPaymentsTotalsMetrics;

  return (
    <div>
      <TabSwitcher
        options={paymentTabOptions}
        value={activeTab}
        onChange={handleTabChange}
        fullWidth
        marginTop={16}
      />

      <div className={styles.contentBox}>
        {activeTab === "dashboard" && (
          <>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <LayoutDashboard size={22} strokeWidth={2} />
                Dashboard
              </h2>
            </div>
            <PaymentsDashboard
              dashboardStartDate={dashboardStartDate}
              dashboardEndDate={dashboardEndDate}
              setDashboardStartDate={setDashboardStartDate}
              setDashboardEndDate={setDashboardEndDate}
              paymentMetrics={paymentMetrics}
            />
          </>
        )}

        {activeTab === "paymentsDue" && (
          <>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <AlertCircle size={22} strokeWidth={2} />
                Payments Due
              </h2>
            </div>
            <PaymentsDueTab
              data={paymentsDueData}
              isLoading={isLoadingPaymentsDue}
              totalCount={paymentsDueTotalRecords}
              currentPage={paymentsDuePage}
              onPageChange={setPaymentsDuePage}
              rowsPerPage={paymentsDueRowsPerPage}
              onRowsChange={setPaymentsDueRowsPerPage}
            />
          </>
        )}

        {(activeTab === "today" || activeTab === "all") && (
          <>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                {activeTab === "today"
                  ? <Calendar size={22} strokeWidth={2} />
                  : <Receipt size={22} strokeWidth={2} />}
                {activeTab === "today" ? "Today's Payments" : "All Payments"}
              </h2>
              <div className={styles.headerActions}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={Download}
                  onClick={handleExportAnonymous}
                >
                  Export Anonymous
                </Button>
                <Button
                  variant="text"
                  size="small"
                  startIcon={Download}
                  onClick={handleExportUnpaid}
                >
                  Export Unpaid
                </Button>
              </div>
            </div>

            <div className={styles.metricsGrid}>
              <div className={styles.metricCard}>
                <h3 className={styles.metricTitle}>Total Payments Due</h3>
                <p className={`${styles.metricValue} ${styles.metricRed}`}>
                  €
                  {currentMetrics.totalPaymentsDue?.data
                    ? parseFloat(currentMetrics.totalPaymentsDue.data).toFixed(2)
                    : "0.00"}
                </p>
              </div>
              <div className={styles.metricCard}>
                <h3 className={styles.metricTitle}>Total Payments Made</h3>
                <p className={`${styles.metricValue} ${styles.metricGreen}`}>
                  €
                  {currentMetrics.totalPaymentsMade?.data
                    ? parseFloat(currentMetrics.totalPaymentsMade.data).toFixed(2)
                    : "0.00"}
                </p>
              </div>
            </div>

            {activeTab === "all" && (
              <div className={styles.filtersGrid}>
                <div className={styles.filterField}>
                  <label className={styles.filterLabel}>Start Date</label>
                  <CustomCalendar
                    value={filters.startDate || null}
                    onChange={(date) => {
                      if (date) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, "0");
                        const d = String(date.getDate()).padStart(2, "0");
                        handleFilterChange("startDate", `${y}-${m}-${d}`);
                      } else {
                        handleFilterChange("startDate", "");
                      }
                    }}
                    placeholder="Start date"
                  />
                </div>
                <div className={styles.filterField}>
                  <label className={styles.filterLabel}>End Date</label>
                  <CustomCalendar
                    value={filters.endDate || null}
                    onChange={(date) => {
                      if (date) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, "0");
                        const d = String(date.getDate()).padStart(2, "0");
                        handleFilterChange("endDate", `${y}-${m}-${d}`);
                      } else {
                        handleFilterChange("endDate", "");
                      }
                    }}
                    placeholder="End date"
                  />
                </div>
              </div>
            )}

            <DataTable
              columns={columns}
              data={paymentCalculations.filteredPayments}
              getRowId={(p) =>
                p.maxPaymentDueId ?? p.consultation?.id ?? p.consultationId
              }
              isLoading={isLoadingPayments}
              emptyTitle="No payment details found"
              emptyText={
                searchQuery ||
                filters.startDate ||
                filters.endDate ||
                filters.paymentStatus
                  ? "Try adjusting your filters to find what you're looking for."
                  : "No payment records are available at this time."
              }
              totalCount={paymentCalculations.totalRecords}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              rowsValue={rowsPerPage}
              onRowsChange={(val) => {
                setRowsPerPage(val);
                setCurrentPage(1);
              }}
              search={{
                value: searchQuery,
                onChange: (value) => {
                  setSearchQuery(value);
                  setCurrentPage(1);
                },
                placeholder: "Search patient name...",
              }}
              filters={filters}
              filtersConfig={FILTERS_CONFIG}
              onFilterChange={handleFilterChange}
              rowActions={rowActions}
              selectable
              selection={selection}
              bulkActions={[
                {
                  label: "Download Receipts",
                  icon: Download,
                  onClick: (selectedItems) => handleBulkDownload(selectedItems),
                },
              ]}
              noScroll
            />
          </>
        )}
      </div>

      <PaymentModal
        open={paymentModalOpen}
        onClose={handleClosePaymentModal}
        selectedPayment={selectedPaymentForModal}
        modalPaymentsDue={modalPaymentsDue}
        isModalPaymentsLoading={isModalPaymentsLoading}
        loadingPaymentId={loadingPaymentId}
        handleConfirmPayment={handleConfirmPayment}
      />
    </div>
  );
};

export { PaymentsPage };
